import { Card, CardContent, Stack, Box, Typography, Divider, alpha, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

type MetricCardProps = {
  icon: React.ReactNode;
  label: string;
  openValue: number;
  closedValue: number;
  color: string;
};

export function MetricCard({ icon, label, openValue, closedValue, color }: MetricCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const total = openValue + closedValue;

  return (
    <Card
      elevation={0}
      sx={{
        background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.12)} 100%)`,
        border: `1px solid ${alpha(color, 0.25)}`,
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 12px 24px ${alpha(color, 0.2)}`,
          border: `1px solid ${alpha(color, 0.4)}`
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: -50,
          right: -50,
          width: 100,
          height: 100,
          borderRadius: "50%",
          background: alpha(color, 0.05),
          pointerEvents: "none"
        }
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 }, position: "relative", zIndex: 1 }}>
        {isMobile ? (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
              <Box
                sx={{
                  borderRadius: 1.5,
                  bgcolor: alpha(color, 0.9),
                  padding: 1,
                  minWidth: 112.61,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 2px 4px ${alpha(color, 0.2)}`,
                  border: `1px solid ${alpha(color, 0.15)}`
                }}
              >
                {icon}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Stack spacing={0.25} sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: 0.3 }}
                >
                  Total
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color }}>
                  {total}
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(color, 0.3) }} />
              <Stack spacing={0.25} sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: 0.3 }}
                >
                  Open
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color }}>
                  {openValue}
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(color, 0.3) }} />
              <Stack spacing={0.25} sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                  sx={{ textTransform: "uppercase", fontSize: "0.6rem", letterSpacing: 0.3 }}
                >
                  Closed
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color }}>
                  {closedValue}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  borderRadius: 1.5,
                  bgcolor: alpha(color, 0.9),
                  padding: 1,
                  minWidth: 112.61,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 2px 4px ${alpha(color, 0.2)}`,
                  border: `1px solid ${alpha(color, 0.15)}`
                }}
              >
                {icon}
              </Box>
            </Stack>

            <Stack spacing={0.75}>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="h4" fontWeight={800} sx={{ color, lineHeight: 1 }}>
                  {total}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Total
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1.5} sx={{ pt: 0.5 }}>
                <Stack spacing={0.25} sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ textTransform: "uppercase", fontSize: "0.55rem", letterSpacing: 0.3 }}
                  >
                    Open
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color }}>
                    {openValue}
                  </Typography>
                </Stack>
                <Divider orientation="vertical" flexItem sx={{ borderColor: alpha(color, 0.3) }} />
                <Stack spacing={0.25} sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ textTransform: "uppercase", fontSize: "0.55rem", letterSpacing: 0.3 }}
                  >
                    Closed
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color }}>
                    {closedValue}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
